import React, { useState, useRef, useCallback } from 'react';
import { validateSvgContent, extractSvgContent, readFileAsText, SvgExtractResult } from '../../utils/svgImportUtils';

type ImportMode = 'file' | 'code';

interface ImportSvgModalProps {
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly onImport: (result: SvgExtractResult, name: string) => void;
}

/* ========== 内联 SVG 图标（替代 emoji） ========== */

/** 文件夹图标 - 用于"选择文件" tab */
const FolderOpenIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: 4 }}>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    <path d="M2 10h20" />
  </svg>
);

/** 代码图标 - 用于"粘贴代码" tab */
const CodeIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: 4 }}>
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

/**
 * 生成安全的预览 HTML
 * 
 * 关键：使用 preserveAspectRatio="xMidYMid meet" 确保 SVG 等比缩放并居中显示
 * - xMidYMid: 水平和垂直都居中
 * - meet: 整个 SVG 内容可见（类似 CSS 的 object-fit: contain）
 */
const buildPreviewHtml = (innerContent: string, width: number, height: number): string => {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" style="width:100%;height:100%;max-width:100%;max-height:180px;display:block;">${innerContent}</svg>`;
};

const ImportSvgModal: React.FC<ImportSvgModalProps> = ({ visible, onClose, onImport }) => {
  const [mode, setMode] = useState<ImportMode>('file');
  const [svgCode, setSvgCode] = useState('');
  const [fileName, setFileName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [previewSvg, setPreviewSvg] = useState('');
  const [previewSize, setPreviewSize] = useState<{ width: number; height: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setSvgCode('');
    setFileName('');
    setErrorMsg('');
    setPreviewSvg('');
    setPreviewSize(null);
    setMode('file');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  /** 校验并设置预览 */
  const processContent = useCallback((content: string, sourceName: string) => {
    setErrorMsg('');
    setPreviewSvg('');
    setPreviewSize(null);

    const validation = validateSvgContent(content);
    if (!validation.valid) {
      setErrorMsg(validation.message);
      return;
    }

    const extracted = extractSvgContent(content);
    const previewHtml = buildPreviewHtml(extracted.innerContent, extracted.width, extracted.height);
    setPreviewSvg(previewHtml);
    setPreviewSize({ width: extracted.width, height: extracted.height });
    setSvgCode(content);
    setFileName(sourceName);
  }, []);

  /** 处理文件选择 */
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const content = await readFileAsText(file);
      const name = file.name.replace(/\.svg$/i, '');
      processContent(content, name);
    } catch (err: any) {
      console.error('读取 SVG 文件失败:', err);
      setErrorMsg(err.message ?? '读取文件失败');
    }
  }, [processContent]);

  /** 处理代码粘贴/输入后的校验 */
  const handleCodeChange = useCallback((value: string) => {
    setSvgCode(value);
    setErrorMsg('');
    setPreviewSvg('');
    setPreviewSize(null);
  }, []);

  /** 手动触发代码预览 */
  const handlePreviewCode = useCallback(() => {
    processContent(svgCode, '手动输入 SVG');
  }, [svgCode, processContent]);

  /** 确认导入 */
  const handleConfirmImport = useCallback(() => {
    const validation = validateSvgContent(svgCode);
    if (!validation.valid) {
      setErrorMsg(validation.message);
      return;
    }

    const result = extractSvgContent(svgCode);
    const name = fileName || `导入SVG_${Date.now()}`;
    onImport(result, name);
    handleClose();
  }, [svgCode, fileName, onImport, handleClose]);

  if (!visible) return null;

  const isConfirmEnabled = svgCode.trim() && !errorMsg;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          width: 520,
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#1e293b' }}>导入 SVG</span>
          <button
            onClick={handleClose}
            style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: '#94a3b8', lineHeight: 1 }}
          >
            ✕
          </button>
        </div>

        {/* 模式切换 Tab */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', padding: '0 20px' }}>
          {(['file', 'code'] as ImportMode[]).map((m) => {
            const isActive = mode === m;
            const tabColor = isActive ? '#3b82f6' : '#64748b';
            return (
              <button
                key={m}
                onClick={() => { setMode(m); setErrorMsg(''); setPreviewSvg(''); setPreviewSize(null); }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: 500,
                  border: 'none',
                  borderBottom: isActive ? '2px solid #3b82f6' : '2px solid transparent',
                  background: 'none',
                  color: tabColor,
                  cursor: 'pointer',
                  transition: 'color 0.2s',
                }}
              >
                {m === 'file' ? (
                  <>
                    <FolderOpenIcon size={14} color={tabColor} />
                    选择文件
                  </>
                ) : (
                  <>
                    <CodeIcon size={14} color={tabColor} />
                    粘贴代码
                  </>
                )}
              </button>
            );
          })}
        </div>

        {/* 内容区 */}
        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {mode === 'file' && (
            <div>
              <label
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed #cbd5e1',
                  borderRadius: 8,
                  padding: '32px 16px',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                  background: '#f8fafc',
                }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const file = e.dataTransfer.files?.[0];
                  if (file && file.name.toLowerCase().endsWith('.svg')) {
                    try {
                      const content = await readFileAsText(file);
                      processContent(content, file.name.replace(/\.svg$/i, ''));
                    } catch (err: any) {
                      setErrorMsg(err.message ?? '读取文件失败');
                    }
                  } else {
                    setErrorMsg('请拖入 .svg 格式的文件');
                  }
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <span style={{ marginTop: 12, fontSize: 13, color: '#64748b' }}>
                  点击选择或拖拽 .svg 文件到此处
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".svg"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </label>
              {fileName && (
                <p style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
                  已选择: <strong>{fileName}.svg</strong>
                </p>
              )}
            </div>
          )}

          {mode === 'code' && (
            <div>
              <textarea
                value={svgCode}
                onChange={(e) => handleCodeChange(e.target.value)}
                placeholder={'粘贴 SVG 代码，例如:\n<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">\n  <circle cx="50" cy="50" r="40" fill="blue"/>\n</svg>'}
                style={{
                  width: '100%',
                  height: 160,
                  fontFamily: 'monospace',
                  fontSize: 12,
                  padding: 12,
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  resize: 'vertical',
                  outline: 'none',
                  lineHeight: 1.5,
                }}
              />
              <button
                onClick={handlePreviewCode}
                disabled={!svgCode.trim()}
                style={{
                  marginTop: 8,
                  padding: '6px 14px',
                  fontSize: 12,
                  border: '1px solid #cbd5e1',
                  borderRadius: 4,
                  background: svgCode.trim() ? '#f1f5f9' : '#f8fafc',
                  color: svgCode.trim() ? '#334155' : '#94a3b8',
                  cursor: svgCode.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                预览
              </button>
            </div>
          )}

          {/* 错误提示 */}
          {errorMsg && (
            <div style={{
              marginTop: 12,
              padding: '8px 12px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 6,
              fontSize: 12,
              color: '#dc2626',
            }}>
              ⚠️ {errorMsg}
            </div>
          )}

          {/* 预览区 */}
          {previewSvg && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: '#64748b' }}>预览:</span>
                {previewSize && (
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>
                    原始尺寸: {previewSize.width} × {previewSize.height}
                  </span>
                )}
              </div>
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 6,
                  padding: 12,
                  background: '#fafafa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 200,
                  overflow: 'hidden',
                  /* 棋盘格背景 - 方便查看透明区域 */
                  backgroundImage: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%, transparent 75%, #f0f0f0 75%), linear-gradient(45deg, #f0f0f0 25%, transparent 25%, transparent 75%, #f0f0f0 75%)',
                  backgroundSize: '16px 16px',
                  backgroundPosition: '0 0, 8px 8px',
                }}
                dangerouslySetInnerHTML={{ __html: previewSvg }}
              />
            </div>
          )}
        </div>

        {/* 底部操作 */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 10,
          padding: '12px 20px',
          borderTop: '1px solid #e2e8f0',
        }}>
          <button
            onClick={handleClose}
            style={{
              padding: '8px 18px',
              fontSize: 13,
              border: '1px solid #cbd5e1',
              borderRadius: 6,
              background: '#fff',
              color: '#475569',
              cursor: 'pointer',
            }}
          >
            取消
          </button>
          <button
            onClick={handleConfirmImport}
            disabled={!isConfirmEnabled}
            style={{
              padding: '8px 18px',
              fontSize: 13,
              border: 'none',
              borderRadius: 6,
              background: isConfirmEnabled ? '#3b82f6' : '#94a3b8',
              color: '#fff',
              cursor: isConfirmEnabled ? 'pointer' : 'not-allowed',
              fontWeight: 500,
            }}
          >
            确认导入
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportSvgModal;